import { css } from '@emotion/core';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import * as React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { clearProfileConflicts, fetchPersonDetails } from '../actions';
import { getProfileConflictDetails } from '../api';
import { zIndexes } from '../common/css-variables';
import { logger } from '../common/logger';
import { ProfileConflictResolver } from '../containers/profile-conflict-resolver';
import { useSlides } from '../hooks/use-candidate-slides';
import { State } from '../state';
import { Spinner } from './spinner';

const resolverStyles = css`
    .profile-comparison {
        display: flex;
        height: 65vh;
    }
`;

interface ConnectedProps {
    conflictIds: [string, string];
}

interface ConnectedDispatch {
    clearProfileConflicts: (resolvedPersonId?: string) => void;
    fetchPersonDetails: (personId: string) => void;
}

type ProfileConflictProps = ConnectedProps & ConnectedDispatch;

const ProfileConflictAlertComponent: React.FC<ProfileConflictProps> = (props) => {
    const history = useHistory();
    const { setList } = useSlides();
    const [resolving, setResolving] = React.useState(false);
    const [closing, setClosing] = React.useState(false);

    React.useEffect(() => {
        setResolving(false);
        setClosing(false);
    }, [props.conflictIds]);

    const handleCloseModal = async () => {
        if (closing) return;
        if (resolving) {
            setClosing(true);
            try {
                const result = await getProfileConflictDetails(...props.conflictIds);
                if (!result.success) throw new Error(result.data?.message || 'Error checking conflict status');
                if (!result.data.resolution.conflictOpen) {
                    handleResolved(result.data.resolution.mergedPersonId);
                    return;
                }
                props.conflictIds.forEach((personId) => props.fetchPersonDetails(personId));
            } catch (err) {
                logger.error(err);
            }
        }
        props.clearProfileConflicts();
    };
    const handleResolve = () => setResolving(true);
    const handleResolved = (mergedPersonId?: string) => {
        const personIds = mergedPersonId ? [mergedPersonId] : props.conflictIds;
        personIds.forEach((personId) => props.fetchPersonDetails(personId));
        if (mergedPersonId) {
            // Clear slides so deleted profiles cannot be revisited, even when the current profile survives.
            setList(null, null);
            if (mergedPersonId !== props.conflictIds[0]) history.push(`/person/${mergedPersonId}`);
        }
        props.clearProfileConflicts(props.conflictIds[0]);
    };

    const dialogContent = closing ? (
        <DialogContent>
            <Spinner />
        </DialogContent>
    ) : resolving && props.conflictIds ? (
        <ProfileConflictResolver
            personId1={props.conflictIds[0]}
            personId2={props.conflictIds[1]}
            onClose={handleCloseModal}
            onResolved={handleResolved}
            onConflictClosed={handleResolved}
        />
    ) : (
        <>
            <DialogContent>Possibly multiple profiles for same person detected.</DialogContent>
            <DialogActions>
                <Button onClick={handleCloseModal}>Close</Button>
                <Button onClick={handleResolve}>Resolve Conflicts</Button>
            </DialogActions>
        </>
    );

    return props.conflictIds ? (
        <Dialog
            open={true}
            onClose={handleCloseModal}
            style={resolving ? undefined : { zIndex: zIndexes.modalDialog }}
            css={resolving ? resolverStyles : undefined}
            maxWidth={resolving ? 'xl' : 'sm'}
            fullWidth={true}
        >
            <DialogTitle>
                <Typography variant="h4" component="div">
                    {resolving ? 'Profile conflict' : 'Potential Profile Conflict'}
                </Typography>
            </DialogTitle>
            {dialogContent}
        </Dialog>
    ) : null;
};

const mapStateToProps = (state: State): ConnectedProps => ({
    conflictIds: state.profileConflicts
});

const mapDispatchToProps: { [action in keyof ConnectedDispatch]: ConnectedDispatch[action] } = {
    clearProfileConflicts,
    fetchPersonDetails
};

export const ProfileConflictAlert = connect<ConnectedProps, ConnectedDispatch, {}>(
    mapStateToProps,
    mapDispatchToProps
)(ProfileConflictAlertComponent);
